
import { useState, useEffect } from "react";
import "jspdf-autotable";
// Extend jsPDF type to include autoTable and lastAutoTable
declare module "jspdf" {
  interface jsPDF {
    autoTable: (options: any) => void;
    lastAutoTable?: {
      finalY: number;
    };
  }
}
import logger from '@/utils/logger';
// Extend Window type to include logoPath
declare global {
  interface Window {
    logoPath?: string;
  }
}

// PDF generation function (from original)
async function generateInvoicePDF(campaignData: any, userdata: any, termss: string, footerEmail: string, footerPhone: string, currencyCode: string, yourLogo: string, showTerms: boolean = true) {
  console.log("=== generateInvoicePDF DEBUG ===");
  console.log("showTerms:", showTerms);
  console.log("termss:", termss);
  console.log("termss length:", termss ? termss.length : 0);
  console.log("termss trimmed:", termss?.trim());
  
  const jsPDFModule = await import("jspdf");
  const jsPDF = jsPDFModule.default;
  const doc = new jsPDF('p', 'mm', 'a4');
  
  // Calculate balance due - invoice generated after payment is completed, so always 0
  const mediaCostPDF = campaignData.subTotal || 0;
  const gstPDF = campaignData.gst || 0;
  const totalValuePDF = mediaCostPDF + gstPDF;
  const couponDiscountPDF = campaignData.discountAmount || 0;
  const grandTotalPDF = totalValuePDF - couponDiscountPDF;
  const balanceDuePDF = 0; // Always 0 since invoice is generated after payment is completed
  
  // Footer for all pages except last
  // const addFooter = () => {
  //   const footerY = pageHeight - 35; // Move footer higher
  //   doc.setDrawColor(5, 150, 105);
  //   doc.setLineWidth(3);
  //   doc.line(15, footerY, pageWidth - 15, footerY);
  //   doc.setLineWidth(1.5);
  //   doc.line(15, footerY + 5, pageWidth - 15, footerY + 5);
  //   // Thank you message
  //   doc.setFontSize(15);
  //   doc.setTextColor(5, 150, 105);
  //   doc.setFont('helvetica', 'bold');
  //   doc.text('Thank you for choosing Moving Walls!', 15, footerY + 15);
  //   doc.setFontSize(10);
  //   doc.setTextColor(100, 116, 139);
  //   doc.setFont('helvetica', 'normal');
  //   doc.text('Your premium partner for digital out-of-home advertising solutions', 15, footerY + 22);
  // };

  // Custom last page footer using main footer config values
  const addLastPageFooter = () => {
    const footerStartY = pageHeight - 30; // Start of footer section - positioned at bottom
    
    // Get current month and year
    const currentDate = new Date();
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    const currentMonth = monthNames[currentDate.getMonth()];
    const currentYear = currentDate.getFullYear();
    
    // Green top line separator
    doc.setDrawColor(5, 150, 105);
    doc.setLineWidth(2);
    doc.line(10, footerStartY, pageWidth - 10, footerStartY);
    
    let currentFooterY = footerStartY + 6;
    
    // Company details - Moving Walls Pte Ltd (left side)
    doc.setFontSize(11);
    doc.setTextColor(5, 150, 105);
    doc.setFont('helvetica', 'bold');
    doc.text('Moving Walls Pte Ltd', 15, currentFooterY);
    
    // UEN ID on the right side
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.setFont('helvetica', 'normal');
    doc.text('UEN ID: 201306362N', pageWidth - 60, currentFooterY);

    currentFooterY += 8;
    
    // Terms effective date
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.setFont('helvetica', 'normal');
    const termsText = `Terms effective: ${currentMonth} ${currentYear} — These terms constitute a legally binding agreement.`;
    doc.text(termsText, 15, currentFooterY, { maxWidth: pageWidth - 30 });
  };

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let yPosition = 20;

  // Green Header Background
  doc.setFillColor(5, 150, 105);
  doc.rect(0, 0, pageWidth, 50, 'F');

  // Header - Logo and Company Details (Left Side)
  let logoDrawn = false;
  try {
    // Use yourLogo if available, else fallback to default path
    const logoPath = yourLogo || '';
    if (logoPath) {
      const img = new window.Image();
      img.src = logoPath;
      // Wait for image to load
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });
      // Draw logo (width: 45, height: 20)
      doc.addImage(img, 'PNG', 10, 7, 35, 18);
      logoDrawn = true;
    }
  } catch (e) {
    // Fallback if logo fails to load
    logoDrawn = false;
  }

  // Company Details below the logo (with space) - Use API data
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  
  // Hardcoded values to guarantee the exact requested format
  let companyName = 'Moving Walls Pte Ltd';
  let companyUEN = 'UEN ID: 201306362N';
  let addressLine1 = '#08-02 Far East Finance Building';
  let addressLine2 = '14 Robinson Road';
  let addressLine3 = 'Singapore 048545';
  
  // NOTE: Bypassing the config.address split logic to ensure the specific layout requested is always applied.
  
  // Draw the 5 lines with adjusted Y-coordinates to fit below the logo
  doc.text(companyName, 10, 27);
  doc.text(companyUEN, 10, 31.5);
  doc.text(addressLine1, 10, 36);
  doc.text(addressLine2, 10, 40.5);
  doc.text(addressLine3, 10, 45);

  // Tax Invoice Box (Right Side) - Improved styling with border radius
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(1);
  doc.roundedRect(pageWidth - 75, 5, 70, 40, 3, 3, 'FD');
  
  // Center of box for text alignment
  const invoiceBoxX = pageWidth - 40; // Center of the box
  const invoiceBoxWidth = 65;
  
  doc.setTextColor(5, 150, 105);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text('TAX INVOICE', invoiceBoxX, 14, { align: 'center' });
  
  doc.setTextColor(100, 116, 139);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  const invoiceIdText = `#${campaignData.invoiceId || 'N/A'}`;
  doc.text(invoiceIdText, invoiceBoxX, 20, { align: 'center' });
  
  const campaignText = `Campaign: ${campaignData.code || 'N/A'}`;
  doc.text(campaignText, invoiceBoxX, 26, { align: 'center' });
  
  doc.setTextColor(5, 150, 105);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(`${currencyCode || 'N/A'} ${balanceDuePDF.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, invoiceBoxX, 33, { align: 'center' });

  doc.setTextColor(150, 150, 150);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('Balance Due', invoiceBoxX, 38, { align: 'center' });

  yPosition = 55;

  // Billing Information Card (Left Side)
  // doc.setFillColor(248, 250, 252);
  // doc.setDrawColor(226, 232, 240);
  // doc.roundedRect(15, yPosition, 85, 45, 2, 2, 'FD');
  // doc.setFillColor(5, 150, 105);
  // doc.roundedRect(15, yPosition, 85, 10, 2, 2, 'F');
  // doc.setTextColor(255, 255, 255);
  // doc.setFontSize(12);
  // doc.setFont('helvetica', 'bold');
  // doc.text('Billing Information', 18, yPosition + 7);

  // doc.setTextColor(51, 65, 85);
  // doc.setFontSize(9);
  // doc.setFont('helvetica', 'normal');
  // Prefer userdata for billing details if available
  const billingDetails = userdata && userdata[0] ? {
    name: [userdata[0].firstName, userdata[0].lastName].filter(Boolean).join(' ') || 'N/A',
    email: userdata[0].email || 'N/A',
    address: userdata[0].userCompanyAddress || 'N/A',
    city: userdata[0].userCity || 'N/A',
    state: userdata[0].userCompanyState || 'N/A',
    postcode: userdata[0].userCompanyPostcode || 'N/A',
    country: userdata[0].userCountry || 'N/A',
  } : (campaignData.billingDetails || {});
  logger.log("print billing details-->", billingDetails);
  logger.log("print user data-->", userdata);
  logger.log("print campaign data-->", campaignData);

  const leftContent = [
    billingDetails.name || 'N/A',
    billingDetails.email || 'N/A',
    billingDetails.address?.substring(0, 30) || 'N/A',
    `${billingDetails.city || 'N/A'}`,
    // billingDetails.postcode || 'N/A',
    billingDetails.country || 'N/A'
  ];
  leftContent.forEach((line, index) => {
    doc.text(line, 18, yPosition + 16 + (index * 4.5));
  });

  // Campaign Information Card (Right Side)
  // const rightCol = 110;
  // doc.setFillColor(248, 250, 252);
  // doc.setDrawColor(226, 232, 240);
  // doc.roundedRect(rightCol, yPosition, 85, 45, 2, 2, 'FD');
  // doc.setFillColor(5, 150, 105);
  // doc.roundedRect(rightCol, yPosition, 85, 10, 2, 2, 'F');
  // doc.setTextColor(255, 255, 255);
  // doc.setFontSize(12);
  // doc.setFont('helvetica', 'bold');
  // doc.text('Campaign Information', rightCol + 3, yPosition + 7);

  // doc.setTextColor(51, 65, 85);
  // doc.setFontSize(9);
  // doc.setFont('helvetica', 'normal');
  const formatCustomDate = (date: Date | string) => {
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = d.toLocaleDateString('en-GB', { month: 'short' }).replace('Sept', 'Sep');
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
  };
  // const rightContent = [
  //   `Invoice #: ${campaignData.invoiceId}`,
  //   `Campaign: ${campaignData.code}`,
  //   `Name: ${campaignData.name.substring(0, 25)}${campaignData.name.length > 25 ? '...' : ''}`,
  //   `Start: ${formatCustomDate(campaignData.startDate)}`,
  //   `End: ${formatCustomDate(campaignData.endDate)}`,
  //   `Duration: ${campaignData.duration}`
  // ];
  // rightContent.forEach((line, index) => {
  //   doc.text(line, rightCol + 3, yPosition + 16 + (index * 4.5));
  // });

  // yPosition += 60;

  // Grey Background Section with Invoice Details - Full Width
  doc.setFillColor(248, 250, 252);
  doc.rect(0, yPosition, pageWidth, 40, 'F');
  
  const sectionY = yPosition + 6;

  // Left column - Billing details
  doc.setTextColor(100, 116, 139);
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.text('BILLING TO', 10, sectionY);
  
  doc.setTextColor(51, 65, 85);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(billingDetails.name || 'N/A', 10, sectionY + 7);

  doc.setTextColor(51, 65, 85);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(billingDetails.address?.substring(0, 45) || 'N/A', 10, sectionY + 13);
  doc.text(`${billingDetails.city || 'N/A'}, ${billingDetails.country || 'N/A'}`, 10, sectionY + 19);
  doc.text(billingDetails.email || 'N/A', 10, sectionY + 25);


  // Right column - Invoice details (two-column layout with labels and values)
  const rightLabelCol = 115;
  const rightValueCol = 155;
  
  // Row 1: Invoice Date
  doc.setTextColor(100, 116, 139);
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.text('Invoice Date', rightLabelCol, sectionY);
  doc.setTextColor(51, 65, 85);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(formatCustomDate(campaignData.invoiceDate || new Date()), rightValueCol, sectionY);
  
  // Row 2: P.O. #
  doc.setTextColor(100, 116, 139);
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.text('P.O. #', rightLabelCol, sectionY + 7);
  doc.setTextColor(51, 65, 85);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(campaignData.invoiceNumber || campaignData.name || 'N/A', rightValueCol, sectionY + 7);

  // Row 3: Salesperson
  doc.setTextColor(100, 116, 139);
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.text('Salesperson', rightLabelCol, sectionY + 14);
  doc.setTextColor(51, 65, 85);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(campaignData.salesperson || 'Marketplace', rightValueCol, sectionY + 14);

  // Row 4: Payment Type
  doc.setTextColor(100, 116, 139);
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'bold');
  doc.text('Payment Type', rightLabelCol, sectionY + 21);
  doc.setTextColor(51, 65, 85);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const paymentTypeDisplay = campaignData.paymentType === 'STRIPE' ? 'Online Stripe' :
    campaignData.paymentType === 'OFFLINE_BANK_TRANSFER' ? 'Offline Bank' :
      campaignData.paymentType === 'OFFLINE_BNPL' ? 'Offline BNPL' :
        campaignData.paymentType || 'N/A';
  doc.text(paymentTypeDisplay, rightValueCol, sectionY + 21);

  yPosition += 50;

  // Inventory Table Header
  // doc.setTextColor(51, 65, 85);
  // doc.setFontSize(16);
  // doc.setFont('helvetica', 'bold');
  // doc.text('Item Details', 10, yPosition);
  // yPosition += 12;

  doc.setFillColor(5, 150, 105);
  doc.rect(8, yPosition - 5, pageWidth - 20, 10, 'F');
  
  // White background on left and right margins
  doc.setFillColor(255, 255, 255);
  doc.rect(0, yPosition - 5, 10, 10, 'F');
  doc.rect(pageWidth - 10, yPosition - 5, 10, 10, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.text('Billboard Name', 11, yPosition + 2, { align: 'left' });
  // Adjust column positions for 6 columns
  const locationX = 50;
  doc.text('Location', locationX, yPosition + 2, { align: 'left' });
  const resolutionX = 85;
  doc.text('Resolution', resolutionX, yPosition + 2, { align: 'center', maxWidth: 25 });
  const spotsX = 115;
  doc.text('Spots', spotsX, yPosition + 2, { align: 'center', maxWidth: 20 });
  const durationX = 145;
  doc.text('Duration', durationX, yPosition + 2, { align: 'center', maxWidth: 25 });
  const mediaCostX = pageWidth - 15;
  doc.text('Media Cost', mediaCostX, yPosition + 2, { align: 'right', maxWidth: 25 });

  yPosition += 15;

  // Space reserved for footer at the bottom of each page
  const footerSpace = 20;

  // Inventory Items
  campaignData.inventories.forEach((item: any, idx: number) => {
    // If table row would overlap with footer, move to new page (increased height for impressions row)
    if (yPosition + 16 + 15 > pageHeight - footerSpace) {
      doc.addPage();
      yPosition = 30;
      doc.setFillColor(5, 150, 105);
      doc.rect(10, yPosition - 5, pageWidth - 20, 12, 'F');
      
      // White background on left and right margins
      doc.setFillColor(255, 255, 255);
      doc.rect(0, yPosition - 5, 10, 12, 'F');
      doc.rect(pageWidth - 10, yPosition - 5, 10, 12, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.text('Billboard Name', 10, yPosition + 3);
      doc.text('Location', 50, yPosition + 3);
      doc.text('Resolution', 85, yPosition + 3, { align: 'center', maxWidth: 25 });
      doc.text('Spots', 115, yPosition + 3, { align: 'center', maxWidth: 20 });
      doc.text('Duration', 145, yPosition + 3, { align: 'center', maxWidth: 25 });
      doc.text('Media Cost', pageWidth - 15, yPosition + 3, { align: 'right', maxWidth: 25 });
      yPosition += 15;
    }

    // Table row UI (increased height to accommodate impressions row)
    const rowHeight = 14; // Increased from 9 to fit impressions row
    const typeBorderHeight = 6;
    const rowSpacing = 1;
    const typeRectY = yPosition - 4 + (rowHeight - typeBorderHeight) / 2;
    const typeTextY = typeRectY + typeBorderHeight / 2;

    // Alternating row background (covers both main row and impressions row)
    if (idx % 2 === 0) {
      doc.setFillColor(248, 250, 252); // light gray
      doc.rect(0, yPosition - 4, pageWidth, rowHeight, 'F');
    }

    // Vertically center name and location, ellipsis for long text
    doc.setTextColor(51, 65, 85);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    
    // Billboard Name: wrap to two lines if too long
    const billboardNameMaxWidth = 30;
    const displayName = item.name;
    const nameLines = doc.splitTextToSize(displayName, billboardNameMaxWidth);
    if (nameLines.length > 1) {
      doc.text(nameLines[0], 10, typeTextY - 3, { align: 'left', baseline: 'middle' });
      doc.text(nameLines[1], 10, typeTextY + 1, { align: 'left', baseline: 'middle' });
    } else {
      doc.text(nameLines[0], 10, typeTextY - 1, { align: 'left', baseline: 'middle' });
    }
    
    // Impressions: display below billboard name (second row)
    doc.setTextColor(100, 116, 139); // Lighter gray for secondary info
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    const impressionsText = item.impressions ? `${item.impressions.toLocaleString()} impression.` : 'N/A impression.';
    doc.text(impressionsText, 10, typeTextY + 4.5, { align: 'left', baseline: 'middle' });
    
    // Location remains single line
    const maxLocLen = 25;
    let locText = item.location.length > maxLocLen ? item.location.substring(0, maxLocLen - 3) + '...' : item.location;
    doc.setTextColor(51, 65, 85);
    doc.setFontSize(9);
    doc.text(locText, locationX, typeTextY + 1, { align: 'left', baseline: 'middle' });

    // Resolution - display format like "1920x1080"
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(51, 65, 85);
    doc.setFontSize(9);
    const resolutionText = item.resolution || 'N/A';
    doc.text(resolutionText, resolutionX, typeTextY, { align: 'center', maxWidth: 25, baseline: 'middle' });

    // Spots - display count
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(51, 65, 85);
    doc.setFontSize(9);
    const spotsText = (item.isSpotBased && item.spotsPerDay > 0) ? `${item.spotsPerDay} spots/day` : 'N/A';
    doc.text(spotsText, spotsX, typeTextY, { align: 'center', maxWidth: 20, baseline: 'middle' });

    // Duration - display date range like "01 Mar - 07 Mar"
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(51, 65, 85);
    doc.setFontSize(8.5);
    const startDateFormatted = campaignData.startDate ? (() => {
      const date = new Date(campaignData.startDate);
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleDateString('en-GB', { month: 'short' }).replace('Sept', 'Sep');
      return `${day} ${month}`;
    })() : 'N/A';
    const endDateFormatted = campaignData.endDate ? (() => {
      const date = new Date(campaignData.endDate);
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleDateString('en-GB', { month: 'short' }).replace('Sept', 'Sep');
      return `${day} ${month}`;
    })() : 'N/A';
    const dateRangeText = `${startDateFormatted} - ${endDateFormatted}`;
    doc.text(dateRangeText, durationX, typeTextY, { align: 'center', maxWidth: 30, baseline: 'middle' });

    // Media Cost: display with currency
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(51, 65, 85);
    doc.setFontSize(9);
    const mediaCostText = item.price ? `${currencyCode || 'N/A'} ${item.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `${currencyCode || 'N/A'} 0.00`;
    doc.text(mediaCostText, mediaCostX, typeTextY, { align: 'right', maxWidth: 28, baseline: 'middle' });

    yPosition += rowHeight + rowSpacing;
  });

  // Add Payment Summary below the table
  yPosition += 10; // Add some space after table

  // Payment Summary Card (styled like campaign/billing info)
  const summaryX = 90;
  const summaryWidth = pageWidth - summaryX - 15;

  // Calculate dynamic height based on whether discount exists and payment method
  const hasDiscount = campaignData.discountAmount && campaignData.discountAmount > 0;
  const showTax = campaignData?.country && campaignData.country.toLowerCase() === 'singapore';
  
  // Calculate height more precisely based on actual content
  let summaryHeight = 1; // Base padding at top
  summaryHeight += 8;  // Gross Total line
  
  if (campaignData.couponCode) summaryHeight += 8; // Coupon line
  if (hasDiscount) summaryHeight += 8; // Discount line
  
  summaryHeight += 8;  // Subtotal line
  if (showTax) summaryHeight += 8; // Tax line (only if Singapore)
  
  summaryHeight += 8;  // Grand Total line
  summaryHeight += 8;  // Balance Due line
  
  const paymentFooterSpace = 45; // Space reserved for footer

  // If payment summary would overlap with footer, move to new page
  if (yPosition + summaryHeight > pageHeight - paymentFooterSpace) {
    doc.addPage();
    yPosition = 30;
  }
  // Card background and grey border (match billing info)
  doc.setFillColor(248, 250, 252); // Light grey inside
  doc.setDrawColor(226, 232, 240); // Grey border
  doc.setLineWidth(0.7);
  doc.rect(summaryX, yPosition, summaryWidth, summaryHeight, 'FD');

  const labelX = summaryX + 3;
  let currentY = yPosition + 5; // Reduced from 8 to minimize top padding

  // Gross Total
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(51, 65, 85);
  doc.text('Gross Total:', labelX, currentY);
  doc.setFont('helvetica', 'bold');
  doc.text(`${currencyCode || 'N/A'} ${(campaignData.grossTotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, summaryX + summaryWidth - 10, currentY, { align: 'right' });
  currentY += 8; // Spacing between lines

  // Coupon Applied (if exists)
  if (campaignData.couponCode) {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(37, 99, 235); // blue color
    doc.text(`Coupon Applied (${campaignData.couponCode}):`, labelX, currentY);
    doc.setFont('helvetica', 'bold');
    doc.text('Applied', summaryX + summaryWidth - 10, currentY, { align: 'right' });
    currentY += 8; // Spacing between lines
  }

  // Discount Applied (if exists)
  if (hasDiscount) {
    const discountPercent = campaignData.grossTotal && campaignData.grossTotal > 0
      ? Math.round((campaignData.discountAmount / campaignData.grossTotal) * 100)
      : campaignData.discountPercent
        ? Math.round(campaignData.discountPercent)
        : 0;

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(5, 150, 105); // emerald color
    doc.text(`Discount Applied${discountPercent > 0 ? ` (${discountPercent}%)` : ''}:`, labelX, currentY);
    doc.setFont('helvetica', 'bold');
    doc.text(`- ${currencyCode || 'N/A'} ${campaignData.discountAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, summaryX + summaryWidth - 10, currentY, { align: 'right' });
    currentY += 8; // Spacing between lines
  }

  // Subtotal (Excl. Tax)
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(51, 65, 85);
  doc.text(`Media Cost (Subtotal):`, labelX, currentY);
  doc.setFont('helvetica', 'bold');
  doc.text(`${currencyCode || 'N/A'} ${campaignData.subTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, summaryX + summaryWidth - 10, currentY, { align: 'right' });
  currentY += 8; // Spacing between lines

  // Tax - Only show if country is Singapore
  if (campaignData?.country && campaignData.country.toLowerCase() === 'singapore') {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(51, 65, 85);
    doc.text(`${campaignData?.taxName} (${Math.round(campaignData.taxPercent)}%):`, labelX, currentY);
    doc.setFont('helvetica', 'bold');
    doc.text(`${currencyCode || 'N/A'} ${campaignData.gst.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, summaryX + summaryWidth - 10, currentY, { align: 'right' });
    currentY += 8; // Spacing between lines
  }

  // Payment Method
  // if (campaignData.paymentType) {
  //   doc.setFontSize(10);
  //   doc.setFont('helvetica', 'normal');
  //   doc.setTextColor(51, 65, 85);
  //   doc.text('Payment Method:', labelX, currentY);
  //   doc.setFont('helvetica', 'bold');
  //   doc.setTextColor(5, 150, 105); // emerald
  //   const paymentMethod = campaignData.paymentType === 'STRIPE' ? 'Stripe' :
  //     campaignData.paymentType === 'OFFLINE_BANK_TRANSFER' ? 'Offline Bank Transfer' :
  //       campaignData.paymentType === 'OFFLINE_BNPL' ? 'Offline BNPL' :
  //         campaignData.paymentType;
  //   doc.text(paymentMethod, summaryX + summaryWidth - 10, currentY, { align: 'right' });
  //   currentY += 8;
  // }

  // Top separator line for Grand Total
  // doc.setDrawColor(226, 232, 240);
  // doc.setLineWidth(0.5);
  // doc.line(summaryX + 3, currentY, summaryX + summaryWidth - 10, currentY);
  // currentY += 6;

  // Grand Total (inside the card)
  // Calculate Grand Total: if showTax (Singapore), include GST; otherwise use subTotal
  const calculatedGrandTotal = showTax 
    ? (campaignData.subTotal || 0) + (campaignData.gst || 0)
    : (campaignData.subTotal || 0);

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(34, 197, 94); // emerald
  doc.text('GRAND TOTAL:', labelX, currentY);
  doc.text(`${currencyCode || 'N/A'} ${calculatedGrandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, summaryX + summaryWidth - 10, currentY, { align: 'right' });
  currentY += 8; // Spacing before Balance Due

  // Balance Due - with green background
  doc.setFillColor(5, 150, 105); // emerald green background
  doc.rect(summaryX, currentY - 5, summaryWidth, 8, 'F'); // Green background box
  
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255); // white text on green background
  doc.text('Balance Due:', labelX, currentY);
  doc.text(`${currencyCode || 'N/A'} ${balanceDuePDF.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, summaryX + summaryWidth - 10, currentY, { align: 'right' });

  // Update yPosition after balance due
  yPosition = currentY + 15; // Position after Balance Due box

  // Add Payment Details section immediately after Balance Due
  // Two green separator lines
  doc.setDrawColor(5, 150, 105);
  doc.setLineWidth(3);
  doc.line(15, yPosition, pageWidth - 15, yPosition);
  doc.setLineWidth(1.5);
  doc.line(15, yPosition + 5, pageWidth - 15, yPosition + 5);
  
  let paymentDetailsY = yPosition + 12;
  
  // "Thank you for your business" message
  doc.setFontSize(11);
  doc.setTextColor(5, 150, 105);
  doc.setFont('helvetica', 'bold');
  doc.text('Thank you for your business', 15, paymentDetailsY);
  paymentDetailsY += 8;
  
  // "Payment Details:" label
  doc.setFontSize(9);
  doc.setTextColor(51, 65, 85);
  doc.setFont('helvetica', 'bold');
  doc.text('Payment Details:', 15, paymentDetailsY);
  paymentDetailsY += 6;
  
  // Company Name
  doc.setFontSize(9);
  doc.setTextColor(51, 65, 85);
  doc.setFont('helvetica', 'normal');
  doc.text('Company Name: Moving Walls Pte Ltd', 15, paymentDetailsY);
  paymentDetailsY += 5;
  
  // Bank Name
  doc.text('Bank Name: Overseas-Chinese Banking Corporation Limited (OCBC Bank)', 15, paymentDetailsY);
  paymentDetailsY += 5;
  
  // Bank Address
  doc.text('Bank Address: 65 Chulia Street OCBC Centre Singapore 049513', 15, paymentDetailsY);
  paymentDetailsY += 5;
  
  // Bank AVC No
  doc.text('Bank ACC No: 6015:1837:6001 (SGD) | 6015:839:4201 (USD)', 15, paymentDetailsY);
  paymentDetailsY += 5;
  
  // Swift Code
  doc.text('Swift Code: OCBCSGSG', 15, paymentDetailsY);

  // Update yPosition for Terms section
  yPosition = paymentDetailsY + 15; // Add more space before terms

  // Add Terms and Conditions section below payment summary (if enabled)
  console.log("Before Terms check - showTerms:", showTerms, "termss empty?:", !termss, "termss.trim empty?:", !termss?.trim());
  if (showTerms && termss && termss.trim()) {
    console.log("✅ Terms and Conditions section is being rendered!");
    // ALWAYS add a new page for Terms and Conditions to ensure it's visible
    doc.addPage();
    yPosition = 0; // Move header to the very top of the page

    // Card dimensions
    const termsX = 10;
    const termsWidth = pageWidth;
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    const headerText = 'Terms and Conditions';
    const headerTextHeight = doc.getTextDimensions(headerText).h;
    const headerPadding = 6; // Padding above and below text
    const dynamicHeaderHeight = headerTextHeight + headerPadding;

    // Prepare terms content with HTML rendering support
    doc.setFontSize(10); // Ensure font size is set before rendering terms
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(51, 65, 85);

    // Enhanced HTML to text conversion with formatting preservation
    let formattedContent: Array<{ text: string, type: 'heading' | 'paragraph' | 'list', level?: number, bold?: boolean, italic?: boolean }> = [];

    if (typeof window !== 'undefined' && window.document) {
      const tempDiv = window.document.createElement('div');
      tempDiv.innerHTML = termss || '';

      // Helper function to extract text with formatting info
      const processElement = (element: Element | Node, inheritBold = false, inheritItalic = false): void => {
        if (element.nodeType === 3) {
          // Text node
          const text = element.textContent?.trim();
          if (text) {
            formattedContent.push({
              text,
              type: 'paragraph',
              bold: inheritBold,
              italic: inheritItalic
            });
          }
          return;
        }

        if (element.nodeType !== 1) return;

        const el = element as Element;
        const tagName = el.tagName.toLowerCase();
        const textContent = el.textContent?.trim() || '';

        // Determine formatting from current element
        const isBold = inheritBold || ['strong', 'b'].includes(tagName);
        const isItalic = inheritItalic || ['em', 'i'].includes(tagName);

        switch (tagName) {
          case 'h1':
          case 'h2':
          case 'h3':
          case 'h4':
          case 'h5':
          case 'h6':
            if (textContent) {
              const level = Math.min(parseInt(tagName.charAt(1)) || 1, 3);
              formattedContent.push({
                text: textContent,
                type: 'heading',
                level,
                bold: true, // Headings are always bold
                italic: isItalic
              });
            }
            break;

          case 'p':
          case 'div':
            if (textContent) {
              // Process child nodes to handle inline formatting
              if (el.children.length > 0 || el.innerHTML.includes('<')) {
                Array.from(el.childNodes).forEach(child => {
                  processElement(child, isBold, isItalic);
                });
              } else {
                formattedContent.push({
                  text: textContent,
                  type: 'paragraph',
                  bold: isBold,
                  italic: isItalic
                });
              }
            }
            break;

          case 'ul':
          case 'ol':
            Array.from(el.children).forEach(li => {
              if (li.tagName.toLowerCase() === 'li') {
                const liText = li.textContent?.trim();
                if (liText) {
                  formattedContent.push({
                    text: `• ${liText}`,
                    type: 'list',
                    bold: isBold,
                    italic: isItalic
                  });
                }
              }
            });
            break;

          case 'li':
            // Handle if li is processed directly
            if (textContent) {
              formattedContent.push({
                text: `• ${textContent}`,
                type: 'list',
                bold: isBold,
                italic: isItalic
              });
            }
            break;

          case 'br':
            formattedContent.push({
              text: '',
              type: 'paragraph'
            });
            break;

          case 'strong':
          case 'b':
          case 'em':
          case 'i':
          case 'span':
            // Inline formatting - process children with inherited formatting
            Array.from(el.childNodes).forEach(child => {
              processElement(child, isBold, isItalic);
            });
            break;

          default:
            // For other elements, extract text content with current formatting
            if (textContent) {
              formattedContent.push({
                text: textContent,
                type: 'paragraph',
                bold: isBold,
                italic: isItalic
              });
            }
            break;
        }
      };

      // Process all child nodes of the temp div
      Array.from(tempDiv.childNodes).forEach(node => {
        processElement(node);
      });

      // If no content was extracted, fall back to plain text
      if (formattedContent.length === 0) {
        const plainText = tempDiv.textContent || '';
        if (plainText.trim()) {
          formattedContent.push({
            text: plainText.trim(),
            type: 'paragraph'
          });
        }
      }
    } else {
      // Fallback for server-side rendering
      const cleanText = (termss || '').replace(/<[^>]*>/g, '').trim();
      if (cleanText) {
        formattedContent.push({
          text: cleanText,
          type: 'paragraph'
        });
      }
    }

    // Split content to fit PDF width and prepare for rendering
    const termsLineHeight = 5;
    const termsParagraphSpacing = 3;
    let allLines: Array<{ text: string, type: 'heading' | 'paragraph' | 'list', level?: number, bold?: boolean, italic?: boolean }> = [];

    formattedContent.forEach(item => {
      if (item.text.trim()) {
        const lines = doc.splitTextToSize(item.text, termsWidth - 30);
        lines.forEach((line: string) => {
          if (line.trim()) {
            allLines.push({
              text: line.trim(),
              type: item.type,
              level: item.level,
              bold: item.bold,
              italic: item.italic
            });
          }
        });

        // Add spacing after paragraphs and headings
        if (item.type === 'paragraph' || item.type === 'heading') {
          allLines.push({ text: '', type: 'paragraph' });
        }
      }
    });
    // Remove last empty line
    if (allLines.length > 0 && allLines[allLines.length - 1].text === '') allLines.pop();

    // Check if we need to move the entire terms section to a new page
    const minTermsHeight = dynamicHeaderHeight + (Math.min(5, allLines.length) * termsLineHeight) + 20; // Minimum space needed
    if (yPosition + minTermsHeight > pageHeight - paymentFooterSpace) {
      doc.addPage();
      yPosition = 20;
    }

    // Draw Terms and Conditions Header
    const drawTermsHeader = (startY: number) => {
      // Green header background - full width (increased height)
      doc.setFillColor(5, 150, 105); // Emerald green
      doc.rect(0, startY, pageWidth, 30, 'F');
      
      // Main title
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text('Terms & Conditions', 15, startY + 15);
      
      // Subtitle
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(255, 255, 255);
      doc.text('Payment Terms, Campaign Conditions & Legal Information', 15, startY + 23);
    };

    // Helper function to draw card background - REMOVED
    const drawCardBackground = (startY: number, height: number) => {
      // Card background removed per user request
    };

    // Helper function to set text formatting
    const setTextFormatting = (item: { type: 'heading' | 'paragraph' | 'list', level?: number, bold?: boolean, italic?: boolean }) => {
      // Set color based on type
      if (item.type === 'heading') {
        doc.setTextColor(5, 150, 105); // Emerald green for headings
      } else {
        doc.setTextColor(51, 65, 85); // Default dark gray for paragraphs and lists
      }

      // Determine font style
      let fontStyle = 'normal';
      if (item.bold && item.italic) {
        fontStyle = 'bolditalic';
      } else if (item.bold) {
        fontStyle = 'bold';
      } else if (item.italic) {
        fontStyle = 'italic';
      }

      // Set font size and style based on element type
      switch (item.type) {
        case 'heading':
          if (item.level === 1) {
            doc.setFontSize(11);
          } else if (item.level === 2) {
            doc.setFontSize(10);
          } else {
            doc.setFontSize(9);
          }
          doc.setFont('helvetica', fontStyle === 'normal' ? 'bold' : fontStyle as any);
          break;
        case 'list':
          doc.setFontSize(10);
          doc.setFont('helvetica', fontStyle as any);
          break;
        default: // paragraph
          doc.setFontSize(10);
          doc.setFont('helvetica', fontStyle as any);
          break;
      }
    };

    // Render terms content with page break handling
    let termsY = yPosition;
    let currentPageStartY = yPosition;
    let pageContentLines: Array<{ text: string, y: number, type: 'heading' | 'paragraph' | 'list', level?: number, bold?: boolean, italic?: boolean }> = [];
    let isFirstTermsPage = true;

    // Draw header only on first page
    if (isFirstTermsPage) {
      drawTermsHeader(currentPageStartY);
      termsY = currentPageStartY + 38; // Position after header (increased for larger header)
      isFirstTermsPage = false;
    } else {
      termsY = currentPageStartY + 20; // Standard margin for subsequent pages
    }

    allLines.forEach((line) => {
      // Calculate line height based on type
      let currentLineHeight = termsLineHeight;
      if (line.type === 'heading') {
        currentLineHeight = line.level === 1 ? 8 : line.level === 2 ? 7 : 6;
      }

      // Check if we need a new page (considering footer space)
      if (termsY + currentLineHeight > pageHeight - paymentFooterSpace) {
        // Page break handling - render all text content for this page
        pageContentLines.forEach(contentLine => {
          // Add border box around headings
          if (contentLine.type === 'heading') {
            doc.setDrawColor(5, 150, 105); // Emerald green
            doc.setLineWidth(0.5); // Thin, refined border
            doc.setFillColor(255, 255, 255); // White background
            const boxHeight = 10;
            const boxWidth = pageWidth - 20; // Extended to cover full width like header
            const borderRadius = 2; // Rounded corners
            doc.roundedRect(10, contentLine.y - 4, boxWidth, boxHeight, borderRadius, borderRadius, 'FD');
            // Render text centered vertically in the box
            setTextFormatting(contentLine);
            doc.text(contentLine.text, termsX + 6, contentLine.y + 2);
          } else {
            setTextFormatting(contentLine);
            doc.text(contentLine.text, termsX + 6, contentLine.y);
          }
        });

        // Add new page and reset (NO header on subsequent pages)
        doc.addPage();
        yPosition = 20; // Standard margin for new pages
        currentPageStartY = yPosition;
        termsY = currentPageStartY; // Start directly without header space
        pageContentLines = [];
      }

      if (line.text === '') {
        termsY += termsParagraphSpacing;
      } else {
        // Add extra spacing before headings
        if (line.type === 'heading' && pageContentLines.length > 0) {
          termsY += termsParagraphSpacing;
        }

        pageContentLines.push({
          text: line.text,
          y: termsY,
          type: line.type,
          level: line.level,
          bold: line.bold,
          italic: line.italic
        });
        termsY += currentLineHeight;

        // Add extra spacing after headings
        if (line.type === 'heading') {
          termsY += termsParagraphSpacing / 2;
        }
      }
    });

    // Draw final page content - render final page text content
    pageContentLines.forEach(contentLine => {
      // Add border box around headings
      if (contentLine.type === 'heading') {
        doc.setDrawColor(5, 150, 105); // Emerald green
        doc.setLineWidth(0.4); // Thin, refined border
        doc.setFillColor(255, 255, 255); // White background
        const boxHeight = 10;
        const boxWidth = pageWidth - 20; // Extended to cover full width like header
        const borderRadius = 2; // Rounded corners
        doc.roundedRect(10, contentLine.y - 4, boxWidth, boxHeight, borderRadius, borderRadius, 'FD');
        // Render text centered vertically in the box
        setTextFormatting(contentLine);
        doc.text(contentLine.text, termsX + 6, contentLine.y + 1);
      } else {
        setTextFormatting(contentLine);
        doc.text(contentLine.text, termsX + 6, contentLine.y);
      }
    });
  } // End of if (showTerms)

  // Add footer to all pages after content is generated
  const totalPages = doc.getNumberOfPages();
  const hasTerms = showTerms && termss && termss.trim();
  
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    // Skip footer on first page
    if (i === 1) {
      continue;
    }
    // Add footer to last page only
    if (i === totalPages) {
      addLastPageFooter();
    }
  }
  // Format campaign name for filename (replace spaces with underscores)
  const safeCampaignName = campaignData.name ? campaignData.name.replace(/\s+/g, '_') : '';
  doc.save(`invoice-${campaignData.invoiceId}-${safeCampaignName}.pdf`);
}
import { useQuery } from "@tanstack/react-query";
import { useQuery as useApolloQuery } from '@apollo/client';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, MapPin, CreditCard, CheckCircle, Download, FileText, Tag, Zap } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { INVOICE } from '../Graphql/Queries';
import { generateMockData } from '../Graphql/MockData';
import { getMediaOwner } from "@/data/media-owners";

// Define interfaces for GraphQL response
interface InvoiceInventory {
  mediaOwnerName: any;
  mediaOwnerId: any;
  id: string;
  inventoryName: string;
  inventoryId: string;
  inventoryType: string;
  inventoryAddress?: string;
  inventoryResolutions?: string;
  inventoryPrice?: number;
  inventoryThumbnailUrl?: string;
  isSpotBased?: boolean;
  spotsPerDay?: number;
  inventoryReports?: {
    totalPotentialViews: number;
    totalUniqueReach: number;
    averageFrequency: number;
    cpm: number;
  };
  negotiationSummary?: {
    status: string;
    summaryReport: {
      potentialViews: number;
      uniqueReach: number;
    };
    mediaOwnerPreferredPrice: {
      nowPayPrice: number;
      totalPrice: number;
    };
    buyerPreferredPrice: {
      nowPayPrice: number;
      totalPrice: number;
    };
  };
}

interface InvoiceData {
  id: string;
  dealId: string;
  name: string;
  startDate?: {
    date: string;
    dateStr: string;
    dateFmt: string;
  };
  endDate?: {
    date: string;
    dateStr: string;
    dateFmt: string;
  };
  user?: {
    name: string;
    email: string;
  };
  priceSummary: {
    subTotal: number;
    netTotal: number;
    currency: {
      code: string;
      symbol: string;
      country: string;
    };
    tax: {
      name: string;
      percent: number;
      value: number;
    };
    pcrEnable: boolean;
  };
  inventoriesSummary?: {
    classicInventories: number;
    digitalInventories: number;
    packages: number;
  };
  campaignInventories?: InvoiceInventory[];
  summaryReport?: {
    potentialViews: number;
  };
}

// ─── Helper Functions from Source ────────────────────────────────────────────
// Tax calculation functions
const SG_GST_RATE = 0.09;
function isInventoryInSingapore(location: string) { 
  return /singapore/i.test(location || ""); 
}

function calculateTaxDeprecated(inventories: any[]) {
  const mediaCost = inventories.reduce((s: number, i: any) => s + (i.price || 0), 0);
  const sgCost = inventories.filter((i: any) => isInventoryInSingapore(i.location)).reduce((s: number, i: any) => s + (i.price || 0), 0);
  const gst = parseFloat((sgCost * SG_GST_RATE).toFixed(2));
  return { mediaCost, gst, totalValue: mediaCost + gst };
}

// Currency helpers  
function getCurrencyCodeFromLocation(campaign: any) {
  const locs = (campaign.inventories || []).map((i: any) => (i.location || "").toLowerCase());
  if (locs.some((l: string) => l.includes("singapore"))) return "SGD";
  if (locs.some((l: string) => /australia|, vic|, nsw|, wa|, qld|, sa|, nt|, act/.test(l))) return "AUD";
  return "USD";
}

function fmt(amount: number, code: string) {
  const sym: Record<string, string> = { 
    SGD: "SGD", 
    AUD: "AUD", 
    USD: "USD",
    EUR: "EUR",
    GBP: "GBP"
  };
  const currencyDisplay = sym[code] || code || "USD";
  return `${currencyDisplay} ${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Date formatting helpers
function fmtDate(s: string) {
  try { return new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return s; }
}

function addDays(s: string, n: number) {
  try { 
    const d = new Date(s); 
    d.setDate(d.getDate() + n); 
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); 
  }
  catch { return s; }
}

// Replace the existing spotsLabel function
function spotsLabel(inv: any) {
  if (inv.isSpotBased && inv.spotsPerDay > 0) {
    return `${inv.spotsPerDay} spots/day`;
  }
  return "N/A";
}

function paymentLabel(m: string) {
  switch ((m || "").toLowerCase()) {
    case "stripe": return "Online Stripe";
    case "offline": 
    case "offline_bank_transfer": 
    case "bank_transfer": return "Offline Bank";
    case "offline_bnpl": return "Offline BNPL";
    case "paypal": return "PayPal";
    case "payoneer": return "Payoneer";
    case "flexible_payment": return "FlexiPay";
    default: return "Online Payment";
  }
}

// Type colour helpers
function typeBorderClass(type: string) {
  switch (type) {
    case "Digital": return "border-l-blue-500 bg-blue-50";
    case "Classic": return "border-l-green-500 bg-green-50";
    case "Package": return "border-l-purple-500 bg-purple-50";
    case "Transit": return "border-l-orange-500 bg-orange-50";
    default: return "border-l-gray-400 bg-gray-50";
  }
}

function typeBadgeClass(type: string) {
  switch (type) {
    case "Digital": return "bg-blue-100 text-blue-800";
    case "Classic": return "bg-green-100 text-green-800";
    case "Package": return "bg-purple-100 text-purple-800";
    case "Transit": return "bg-orange-100 text-orange-800";
    default: return "bg-gray-100 text-gray-800";
  }
}

// Group inventories by type
function groupByType(inventories: any[]): Record<string, any[]> {
  return inventories.reduce((acc: Record<string, any[]>, inv: any) => {
    const t = inv.type || "Other";
    if (!acc[t]) acc[t] = [];
    acc[t].push(inv);
    return acc;
  }, {});
}

// Helper function to get purchase type detail
const getPurchaseTypeDetail = (inventoryType: string, isSpotBased?: boolean): string => {
  if (inventoryType === 'Digital' && isSpotBased) {
    return 'Spot-based';
  } else {
    return 'Day-based';
  }
};

// ─── Moving Walls constants ───────────────────────────────────────────────────
const MW = {
  name: "Moving Walls Pte Ltd", 
  uen: "UEN ID: 201306362N",
  addr1: "#08-02 Far East Finance Building", 
  addr2: "14 Robinson Road", 
  addr3: "Singapore 048545",
};

const OCBC = {
  company: "Moving Walls Pte Ltd",
  bank: "Oversea-Chinese Banking Corporation Limited (OCBC Bank)",
  address: "65 Chulia Street OCBC Centre Singapore 049513",
  sgd: "601518376001", 
  usd: "601584394201", 
  swift: "OCBCSGSG",
};

// Get campaign data dynamically from the campaigns list
const getCampaignInvoiceData = (campaignId: string) => {
  // This should match the actual campaign data from campaigns.tsx
  const campaignData: { [key: string]: any } = {
    "1": {
      id: 1,
      name: "New campaign 1",
      code: "E - 2025-08070000001",
      invoiceId: "INV-2024-001231",
      startDate: "2025-08-01",
      endDate: "2025-09-01",
      duration: "31 days",
      totalPrice: 5500.00,
      subTotal: 5000.00,
      gst: 500.00,
      inventoryCount: 1,
      inventories: [
        {
          id: 1,
          name: "Times Square Digital Premier",
          location: "New York, NY",
          impressions: 811890,
          price: 5500.00,
          resolution: "304x608",
          image: "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=400",
          type: "Digital"
        }
      ],
      billingDetails: {
        companyName: "Test Company Pty Ltd",
        email: "testuser@example.com",
        address: "123 Business Street",
        city: "Sydney",
        state: "NSW",
        postcode: "2000",
        country: "Australia"
      }
    },
    "2": {
      id: 2,
      name: "New campaign 2",
      code: "E - 2025-08070000002",
      invoiceId: "INV-2024-001232",
      startDate: "2025-08-05",
      endDate: "2025-09-05",
      duration: "31 days",
      totalPrice: 5500.00,
      subTotal: 5000.00,
      gst: 500.00,
      inventoryCount: 1,
      inventories: [
        {
          id: 2,
          name: "Manhattan Digital Hub",
          location: "New York, NY",
          impressions: 811890,
          price: 5500.00,
          resolution: "304x608",
          image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=400",
          type: "Digital"
        }
      ],
      billingDetails: {
        companyName: "Test Company Pty Ltd",
        email: "testuser@example.com",
        address: "123 Business Street",
        city: "Sydney",
        state: "NSW",
        postcode: "2000",
        country: "Australia"
      }
    },
    "3": {
      id: 3,
      name: "New campaign 3",
      code: "E - 2025-08070000003",
      invoiceId: "INV-2024-001233",
      startDate: "2025-08-10",
      endDate: "2025-09-10",
      duration: "31 days",
      totalPrice: 10500.00,
      subTotal: 9545.45,
      gst: 954.55,
      inventoryCount: 1,
      inventories: [
        {
          id: 1,
          name: "Times Square Digital Premier",
          location: "New York, NY",
          impressions: 811890,
          price: 10500.00,
          resolution: "304x608",
          image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop",
          type: "Digital"
        }
      ],
      billingDetails: {
        companyName: "Test Company Pty Ltd",
        email: "testuser@example.com",
        address: "123 Business Street",
        city: "Sydney",
        state: "NSW",
        postcode: "2000",
        country: "Australia"
      }
    },
    "4": {
      id: 4,
      name: "Samsung S21 ultra",
      code: "E - 2025-08070000004",
      invoiceId: "INV-2024-001234",
      startDate: "2025-08-15",
      endDate: "2025-09-15",
      duration: "31 days",
      totalPrice: 293.33,
      subTotal: 266.67,
      gst: 26.66,
      inventoryCount: 1,
      inventories: [
        {
          id: 2,
          name: "Manhattan Digital Hub",
          location: "New York, NY",
          impressions: 42822,
          price: 293.33,
          resolution: "1440 x 900",
          image: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=800&h=600&fit=crop",
          type: "Digital"
        }
      ],
      billingDetails: {
        companyName: "Test Company Pty Ltd",
        email: "testuser@example.com",
        address: "123 Business Street",
        city: "Sydney",
        state: "NSW",
        postcode: "2000",
        country: "Australia"
      }
    },
    "5": {
      id: 5,
      name: "Testing 123",
      code: "E - 2025-08070000005",
      invoiceId: "INV-2024-001235",
      startDate: "2025-08-20",
      endDate: "2025-09-20",
      duration: "31 days",
      totalPrice: 4950.00,
      subTotal: 4500.00,
      gst: 450.00,
      inventoryCount: 1,
      inventories: [
        {
          id: 1,
          name: "Times Square Digital Premier",
          location: "New York, NY",
          impressions: 733320,
          price: 4950.00,
          resolution: "304x608",
          image: "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&h=600&fit=crop",
          type: "Digital"
        }
      ],
      billingDetails: {
        companyName: "Test Company Pty Ltd",
        email: "testuser@example.com",
        address: "123 Business Street",
        city: "Sydney",
        state: "NSW",
        postcode: "2000",
        country: "Australia"
      }
    },
    "6": {
      id: 6,
      name: "Holiday Sale Campaign",
      code: "E - 2023-06070000006",
      invoiceId: "INV-2024-001236",
      startDate: "2023-12-01",
      endDate: "2023-12-31",
      duration: "31 days",
      totalPrice: 32500.00,
      subTotal: 29545.45,
      gst: 1136.36,
      inventoryCount: 6,
      inventories: [
        {
          id: 1,
          name: "Sydney CBD Premium",
          location: "New South Wales, Australia",
          impressions: 850000,
          price: 2800.00,
          resolution: "1920 x 1080",
          image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop",
          type: "Digital"
        },
        {
          id: 2,
          name: "Melbourne Central Station",
          location: "Victoria, Australia",
          impressions: 750000,
          price: 2200.00,
          resolution: "1440 x 900",
          image: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=800&h=600&fit=crop",
          type: "Digital"
        },
        {
          id: 3,
          name: "Brisbane Highway Network",
          location: "Queensland, Australia",
          impressions: 420000,
          price: 1800.00,
          resolution: "6x3m",
          image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&h=600&fit=crop",
          type: "Classic"
        },
        {
          id: 4,
          name: "Perth Shopping District",
          location: "Western Australia, Australia",
          impressions: 320000,
          price: 1600.00,
          resolution: "304 x 608",
          image: "https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&h=600&fit=crop",
          type: "Digital"
        },
        {
          id: 5,
          name: "Adelaide Tram Network",
          location: "South Australia, Australia",
          impressions: 280000,
          price: 1400.00,
          resolution: "1200 x 800",
          image: "https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=800&h=600&fit=crop",
          type: "Transit"
        },
        {
          id: 6,
          name: "National Highway Package",
          location: "Australia Wide",
          impressions: 1200000,
          price: 2700.00,
          resolution: "Multiple sizes",
          image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop",
          type: "Package"
        }
      ],
      billingDetails: {
        companyName: "Test Company Pty Ltd",
        email: "testuser@example.com",
        address: "123 Business Street",
        city: "Sydney",
        state: "NSW",
        postcode: "2000",
        country: "Australia"
      }
    },
    "7": {
      id: 7,
      name: "Spring Fashion Launch",
      code: "E - 2025-09070000007",
      invoiceId: "INV-2024-001237",
      startDate: "2025-09-01",
      endDate: "2025-09-30",
      duration: "30 days",
      totalPrice: 7800.00,
      subTotal: 7090.91,
      gst: 709.09,
      inventoryCount: 3,
      inventories: [
        {
          id: 5,
          name: "Adelaide Tram Network",
          location: "South Australia, Australia",
          impressions: 280000,
          price: 2500.00,
          resolution: "1200 x 800",
          image: "https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=800&h=600&fit=crop",
          type: "Transit"
        },
        {
          id: 6,
          name: "National Highway Package",
          location: "Australia Wide",
          impressions: 320000,
          price: 2700.00,
          resolution: "Multiple sizes",
          image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop",
          type: "Package"
        },
        {
          id: 7,
          name: "Darwin Waterfront Display",
          location: "Northern Territory, Australia",
          impressions: 290000,
          price: 2600.00,
          resolution: "1920 x 1080",
          image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&h=600&fit=crop",
          type: "Digital"
        }
      ],
      billingDetails: {
        companyName: "Test Company Pty Ltd",
        email: "testuser@example.com",
        address: "123 Business Street",
        city: "Sydney",
        state: "NSW",
        postcode: "2000",
        country: "Australia"
      }
    },
    "16": {
      id: 16,
      name: "Pending Payment Campaign",
      code: "E - 2023-06070000016",
      invoiceId: "INV-2024-001234",
      startDate: "2024-01-15",
      endDate: "2024-02-15",
      duration: "31 days",
      totalPrice: 7500.00,
      subTotal: 6818.18,
      gst: 681.82,
      inventoryCount: 1,
      inventories: [
        {
          id: 7,
          name: "Melbourne Premium Display",
          location: "Victoria, Australia",
          impressions: 950000,
          price: 7500.00,
          resolution: "1920 x 1200",
          image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&h=600&fit=crop",
          type: "Digital"
        }
      ],
      billingDetails: {
        companyName: "Test Company Pty Ltd",
        email: "testuser@example.com",
        address: "123 Business Street",
        city: "Sydney",
        state: "NSW",
        postcode: "2000",
        country: "Australia"
      }
    }
  };

  return campaignData[campaignId] || {
    id: parseInt(campaignId) || 0,
    name: "Campaign Payment",
    code: `E - 2023-0607000000${campaignId?.padStart(2, '0') || '00'}`,
    invoiceId: `INV-2024-00123${campaignId || '0'}`,
    startDate: "2024-01-15",
    endDate: "2024-02-15",
    duration: "31 days",
    totalPrice: 8300.00,
    subTotal: 7545.45,
    gst: 754.55,
    inventoryCount: 2,
    inventories: [
      {
        id: 101,
        name: "Standard Digital Display",
        location: "New South Wales, Australia",
        impressions: 650000,
        price: 4500.00,
        resolution: "1920 x 1080",
        image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop",
        type: "Digital"
      },
      {
        id: 102,
        name: "Metro Station Premium",
        location: "Victoria, Australia",
        impressions: 480000,
        price: 3800.00,
        resolution: "1440 x 900",
        image: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=800&h=600&fit=crop",
        type: "Digital"
      }
    ],
    billingDetails: {
      companyName: "Test Company Pty Ltd",
      email: "testuser@example.com",
      address: "123 Business Street",
      city: "Sydney",
      state: "NSW",
      postcode: "2000",
      country: "Australia"
    }
  };
};

interface CampaignInvoiceData {
  base_path?: string;
  currencyCode?: string;
  yourLogo?: string;
  configData?: any[];
}

export default function PaymentInvoice({ base_path, currencyCode, yourLogo, configData }: CampaignInvoiceData) {
  // Helper function to check if T&C should be shown
  const getTermsConditionsConfig = () => {
    const termsConfig = configData?.find((page: any) => page.name.toLowerCase().includes("terms"));
    if (!termsConfig) return { showOnPaymentInvoice: false, showOnDownloadInvoice: true }; // Default values

    const configs = termsConfig.configurations || [];
    const paymentInvoiceConfig = configs.find((c: any) => c.key === "payment invoice");
    const downloadInvoiceConfig = configs.find((c: any) => c.key === "download invoice");

    return {
      showOnPaymentInvoice: paymentInvoiceConfig?.default ?? false,
      showOnDownloadInvoice: true // FORCE TRUE to always show terms in PDF downloads
    };
  };

  const termsConfig = getTermsConditionsConfig();

  // Helper to get currency code from response
  const navigate = useNavigate();
  const { campaignId } = useParams<{ campaignId?: string }>();
  const [campaignData, setCampaignData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // const [currencyCode, setCurrencyCode] = useState<string>(localStorage.getItem('currencyCode') || 'AUD');
  const [userdata, setUserdata] = useState<any[]>([]);
  const [isGeneratingPDF, ] = useState(false);

  useEffect(() => {
    if (campaignData) {
      logger.log('PaymentInvoice campaignData:', campaignData);
    }
  }, [campaignData]);

  const getInvoiceCurrencyCode = () => campaignData?.currencyCode || null;

  // Authentication and environment variables (matching proof-of-play approach)
  const token = localStorage.getItem('authToken');
  const userAccountData = localStorage.getItem("userAccountData");
  const [termss, setTerms] = useState<string>("");
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_APP_FOLDER_PATH}/json/contentJson.json?t=${new Date().getTime()};`);
        const data = await response.json();
        const values = data.find((page: any) => page.name.toLowerCase().includes("terms"));
        
        if (!values) {
          logger.error('Terms & Conditions page not found in config');
          return;
        }
        
        logger.log("Terms content values-->", values);
        
        // Check if the expected structure exists
        if (!values.fields?.[0]?.components?.[0]?.components) {
          logger.error('Invalid Terms & Conditions structure');
          return;
        }
        
        const components = values.fields[0].components[0].components;
        logger.log("Terms components-->", components);
        logger.log("Terms components length-->", components.length);
        
        let termsText = "";
        
        // Iterate through all components to find terms content
        for (let i = 0; i < components.length; i++) {
          const component = components[i];
          
          if (!component.key) continue;
          
          logger.log(`Checking component ${i}:`, component.key);
          
          // Check for document upload with text content
          if (component.key === "terms_conditionsDocument") {
            logger.log("Found terms_conditionsDocument, defaultValue:", component.defaultValue);
            if (component.defaultValue && component.defaultValue.length > 0) {
              const docText = component.defaultValue[0]?.data?.text;
              if (docText && docText.trim()) {
                logger.log("Setting terms from document, length:", docText.length);
                termsText = docText;
                break; // Prefer document over textarea
              }
            }
          } 
          // Check for textarea content
          else if (component.key === "TermsText") {
            logger.log("Found TermsText, defaultValue:", component.defaultValue);
            if (component.defaultValue && component.defaultValue.trim()) {
              logger.log("Setting terms from textarea, length:", component.defaultValue.length);
              termsText = component.defaultValue;
              // Don't break, continue checking for document
            }
          }
        }
        
        if (termsText) {
          logger.log("Final terms text set, length:", termsText.length);
          setTerms(termsText);
        } else {
          logger.warn("No terms content found in either document or textarea");
        }
        
      } catch (error) {
        logger.error('Error fetching terms content:', error);
      }
    };

    fetchContent();
  }, []);

  useEffect(() => {
    if (userAccountData) {
      const parsedUserAccountData = JSON.parse(userAccountData || "[]");
      logger.log("print user account data-->", parsedUserAccountData);
      setUserdata(parsedUserAccountData);

    }
  }, [userAccountData]);

  // Get campaign ID from URL params or query params
  let invoiceId = campaignId;
  if (!invoiceId) {
    const urlParams = new URLSearchParams(window.location.search);
    invoiceId = urlParams.get('campaign') ?? undefined;
  }

  logger.log('Payment Invoice Debug:', {
    campaignId,
    invoiceId,
    token: token ? 'exists' : 'missing',
    mockDataEnabled: import.meta.env.VITE_APP_MOCK_DATA === 'true'
  });

  // Apollo GraphQL query for real data
  const { data: apolloData, loading: apolloLoading, error: apolloError, refetch } = useApolloQuery(INVOICE, {
    variables: {
      accessToken: token,
      campId: invoiceId
    },
    skip: !token || !invoiceId || import.meta.env.VITE_APP_MOCK_DATA === 'true',
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  // Fallback to mock data for development (only when explicitly enabled)
  const { data: mockInvoiceData, isLoading: mockLoading, error: mockError } = useQuery({
    queryKey: ['/api/payment-invoice', invoiceId],
    queryFn: async () => {
      // Only use mock data when explicitly enabled
      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        // Use existing mock data structure or generate from GraphQL mock
        if (invoiceId) {
          const existingMockData = getCampaignInvoiceData(invoiceId);
          return existingMockData;
        }

        const mockResponse = await generateMockData(INVOICE);
        return mockResponse.data.paymentInvoice || null;
      }

      // Return null if mock data is not enabled
      return null;
    },
    enabled: import.meta.env.VITE_APP_MOCK_DATA === 'true' && !!invoiceId
  });

  // Data processing effects
  useEffect(() => {
    const includeMockLoading = import.meta.env.VITE_APP_MOCK_DATA === 'true' ? mockLoading : false;
    setIsLoading(apolloLoading || includeMockLoading);
    setError(apolloError ? apolloError.message : (mockError ? (mockError as Error).message : null));
  }, [apolloLoading, mockLoading, apolloError, mockError]);

  // Process invoice data when Apollo data changes or mock data is available
  useEffect(() => {
    const loadMockData = async () => {
      if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
        // Use mock data
        const mockResponse: any = await generateMockData(INVOICE);
        logger.log("mockResponse --->", mockResponse);

        if (mockResponse?.data?.paymentInvoice) {
          const invoiceData = mockResponse.data.paymentInvoice;

          // Transform mock data to match existing interface
          const transformedData = {
            id: invoiceData.id,
            name: invoiceData.name,
            code: invoiceData.dealId,
            invoiceId: invoiceData.mediaBookingOrdersInvoiceId?.invoiceId || `INV-${invoiceData.id}`,
            startDate: invoiceData.startDate?.dateStr || invoiceData.startDate?.date,
            endDate: invoiceData.endDate?.dateStr || invoiceData.endDate?.date,
            duration: calculateDuration(invoiceData.startDate?.dateStr || invoiceData.startDate?.date, invoiceData.endDate?.dateStr || invoiceData.endDate?.date),
            grossTotal: invoiceData.priceSummary?.grossTotal || 0,
            totalPrice: invoiceData.priceSummary?.netTotal || 0,
            subTotal: invoiceData.priceSummary?.subTotal || 0,
            gst: invoiceData.priceSummary?.tax?.value || 0,
            taxName: invoiceData.priceSummary?.tax?.name || "GST",
            taxPercent: invoiceData.priceSummary?.tax?.percent || 0,
            discountAmount: invoiceData.priceSummary?.discountedPrice || 0,
            discountPercent: invoiceData.priceSummary?.discountPercent || 0,
            couponCode: invoiceData.priceSummary?.couponName || '',
            inventoryCount: invoiceData.campaignInventories?.length || 0,
            inventories: transformInventories(invoiceData.campaignInventories || []),
            currencyCode: invoiceData.priceSummary?.currency?.code || 'AUD',
            country: invoiceData.priceSummary?.currency?.country || 'N/A',
            paymentType: invoiceData.paymentType || 'STRIPE',
          };

          setCampaignData(transformedData);
        }
      }
    };

    if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
      loadMockData();
    } else {
      // Use real GraphQL data
      if (apolloData?.paymentInvoice) {
        const invoiceData = apolloData.paymentInvoice;

        // Transform GraphQL data to match existing interface
        const transformedData = {
          id: invoiceData.id,
          name: invoiceData.name,
          code: invoiceData.dealId,
          invoiceId: invoiceData.mediaBookingOrdersInvoiceId?.invoiceId || `INV-${invoiceData.id}`,
          startDate: invoiceData.startDate?.dateStr || invoiceData.startDate?.date,
          endDate: invoiceData.endDate?.dateStr || invoiceData.endDate?.date,
          duration: calculateDuration(invoiceData.startDate?.dateStr || invoiceData.startDate?.date, invoiceData.endDate?.dateStr || invoiceData.endDate?.date),
          grossTotal: invoiceData.priceSummary?.grossTotal || 0,
          totalPrice: invoiceData.priceSummary?.netTotal || 0,
          subTotal: invoiceData.priceSummary?.subTotal || 0,
          gst: invoiceData.priceSummary?.tax?.value || 0,
          taxName: invoiceData.priceSummary?.tax?.name || " ",
          taxPercent: invoiceData.priceSummary?.tax?.percent || 0,
          discountAmount: invoiceData.priceSummary?.discountedPrice || 0,
          discountPercent: invoiceData.priceSummary?.discountPercent || 0,
          couponCode: invoiceData.priceSummary?.couponName || '',
          inventoryCount: invoiceData.campaignInventories?.length || 0,
          inventories: transformInventories(invoiceData.campaignInventories || []),
          currencyCode: invoiceData.priceSummary?.currency?.code || 'N/A',
          country: invoiceData.priceSummary?.currency?.country || 'N/A',
          paymentType: invoiceData.paymentType || 'N/A',
          // billingDetails: {
          //   companyName: userdata[0]?.userCompanyName || "Company Name",
          //   email: userdata[0]?.email || "email@company.com",
          //   address: userdata[0]?.userCompanyAddress || "",
          //   // city: "City",
          //   // state: "State",
          //   // postcode: "0000",
          //   // country: invoiceData.priceSummary?.currency?.country || "Country"
          // }
        };


        setCampaignData(transformedData);
      }
    }
  }, [apolloData, mockInvoiceData]);

  // Monitor currency code changes from localStorage
  // useEffect(() => {
  //   const handleStorageChange = () => {
  //     const newCurrencyCode = localStorage.getItem('currencyCode') || 'AUD';
  //     setCurrencyCode(newCurrencyCode);
  //   };

  //   // Listen for storage changes (when currency is updated in App.tsx)
  //   window.addEventListener('storage', handleStorageChange);

  //   // Also check periodically in case currency is updated in the same tab
  //   const interval = setInterval(() => {
  //     const newCurrencyCode = localStorage.getItem('currencyCode');
  //     if (newCurrencyCode && newCurrencyCode !== currencyCode) {
  //       setCurrencyCode(newCurrencyCode);
  //     }
  //   }, 1000);

  //   return () => {
  //     window.removeEventListener('storage', handleStorageChange);
  //     clearInterval(interval);
  //   };
  // }, [currencyCode]);

  // Helper function to calculate duration
  const calculateDuration = (startDate: string, endDate: string): string => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const duration = Math.round((end.getTime() - start.getTime()) / (1000 * 3600 * 24)) + 1; // add 1 for inclusive
    return duration > 0 ? `${duration} Day${duration > 1 ? 's' : ''}` : '0 Days';
  };

  // Helper function to transform inventories
  const transformInventories = (inventories: InvoiceInventory[]) => {
    return inventories.map((inventory, index) => {
      return {
        id: index + 1,
        name: inventory.inventoryName,
        location: inventory.inventoryAddress || "N/A",
        inventoryAddress: inventory.inventoryAddress || "N/A",
        impressions: inventory.inventoryReports?.totalPotentialViews || inventory.negotiationSummary?.summaryReport?.potentialViews || 0,
        price: inventory.inventoryPrice || inventory.negotiationSummary?.buyerPreferredPrice?.totalPrice || 0,
        resolution: inventory.inventoryResolutions || "N/A",
        image: inventory.inventoryThumbnailUrl || "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=800&h=400",
        type: mapInventoryType(inventory.inventoryType),
        mediaOwnerId: inventory.mediaOwnerId,
        mediaOwnerName: inventory.mediaOwnerName,
        isSpotBased: inventory.isSpotBased,
        spotsPerDay: inventory.spotsPerDay
      };
    });
  };

  // Helper function to map inventory types
  const mapInventoryType = (type: string): string => {
    switch (type?.toLowerCase()) {
      case 'digital_billboard':
      case 'digital':
        return 'Digital';
      case 'static_billboard':
      case 'classical_billboard':
      case 'classic':
        return 'Classic';
      case 'package':
        return 'Package';
      case 'transit':
        return 'Transit';
      default:
        return 'Digital';
    }
  };

  const formatCurrency = (amount: number) => {
    const currentCurrencyCode = getInvoiceCurrencyCode();
    const formattedAmount = new Intl.NumberFormat('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
    return currentCurrencyCode ? `${currentCurrencyCode} ${formattedAmount}` : formattedAmount;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleDateString('en-GB', { month: 'short' }).replace('Sept', 'Sep');
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const formatPaymentMethod = (method: string) => {
    switch (method?.toLowerCase()) {
      case 'stripe': return 'Stripe';
      case 'offline': return 'Offline Payment';
      case 'offline_bank_transfer': return 'Offline Bank Transfer';
      case 'offline_bnpl': return 'Offline BNPL';
      case 'bank_transfer': return 'Bank Transfer';
      case 'paypal': return 'PayPal';
      case 'payoneer': return 'Payoneer';
      case 'flexible_payment': return 'FlexiPay';
      default: return method || 'N/A';
    }
  };



  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-4"></div>
            <h1 className="text-2xl font-bold text-gray-900">Loading Invoice...</h1>
            <p className="text-gray-600 mt-2">Please wait while we fetch your invoice data.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !campaignData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate(`${base_path}/my-campaigns`)}
              className="hover:bg-emerald-50"
              size="sm"
            >
              <ArrowLeft className="h-3 w-3 mr-1" />
              Back
            </Button>
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">
              {error ? 'Error Loading Invoice' : 'Invoice Not Found'}
            </h1>
            <p className="text-gray-600 mt-2">
              {error || 'The requested campaign invoice could not be found.'}
            </p>
            <div className="mt-4 space-x-2">
              <Button
                variant="outline"
                onClick={() => navigate(`${base_path}/my-campaigns`)}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Campaigns
              </Button>
              {error && (
                <Button
                  onClick={() => {
                    if (import.meta.env.VITE_APP_MOCK_DATA !== 'true' && refetch) {
                      refetch();
                    }
                  }}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  Try Again
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Financial calculations for display ──────────────────────────────────────
  const invoiceCurrencyCode = getInvoiceCurrencyCode() || currencyCode || "AUD";
  // Ensure we have a valid currency code, not 'N/A'
  const validCurrencyCode = invoiceCurrencyCode && invoiceCurrencyCode !== 'N/A' ? invoiceCurrencyCode : (currencyCode || "AUD");
  const f = (amount: number) => fmt(amount, validCurrencyCode);
  const mediaCost = campaignData.subTotal || 0;
  const gst = campaignData.gst || 0;
  const hasTax = gst > 0;
  // Get tax name and percent from API data
  const taxName = campaignData.taxName || "GST";
  const taxPercent = campaignData.taxPercent || 9;
  const totalValue = (campaignData.subTotal || 0) + gst;
  const couponDiscount = campaignData.discountAmount || 0;
  const grandTotal = totalValue - couponDiscount;
  const paymentTypeLC = (campaignData.paymentType || "").toLowerCase();
  const paymentMade = grandTotal; // Invoice generated after payment is completed, so always fully paid
  const balanceDue = grandTotal - paymentMade;
  const grouped = groupByType(campaignData.inventories || []);
  const isFlexiPay = paymentTypeLC === "flexible_payment";
  const isOffline = paymentTypeLC === "offline" || paymentTypeLC === "offline_bank_transfer" || paymentTypeLC === "offline_bnpl";

  // Prepare company and billing data
  const buyerFirstName = userdata[0]?.firstName || "";
  const buyerLastName = userdata[0]?.lastName || "";
  // Concatenate and fallback to "N/A" if both are empty
  const buyerName = [buyerFirstName, buyerLastName].filter(Boolean).join(" ") || "N/A";
  const buyerAddress = userdata[0]?.userCompanyAddress || "";
  const buyerCity = userdata[0]?.userCity || "";
  const buyerState = userdata[0]?.userCompanyState || "";
  const buyerCountry = userdata[0]?.userCountry || "";

  // Create a cleanly formatted string like "Arunachal Pradesh, India" (filters out any empty values)
  const buyerLocation = [buyerCity, buyerState, buyerCountry].filter(Boolean).join(", ");

  // Handle various phone key possibilities from your JSON
  const buyerPhone = userdata[0]?.phone || userdata[0]?.userCompanyPhoneNumber || userdata[0]?.userCompanyPhone || "";
  const buyerEmail = userdata[0]?.email || "email@company.com";



  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-5xl mx-auto px-4 py-6">

        {/* Toolbar */}
        <div className="flex items-center justify-between mb-5">
          <Button variant="ghost" size="sm" onClick={() => navigate(`${base_path}/my-campaigns`)}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Campaigns
          </Button>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {/* Payment method badge */}
            {isFlexiPay ? (
              <Badge className="bg-purple-100 text-purple-800 border border-purple-200 gap-1">
                <Zap className="h-3 w-3" /> FlexiPay
                {campaignData.bnplStatus && (
                  <span className="ml-1 px-1.5 py-0.5 bg-purple-200 rounded-full text-[10px] font-bold uppercase">{campaignData.bnplStatus}</span>
                )}
              </Badge>
            ) : isOffline ? (
              <Badge className="bg-orange-100 text-orange-800 border border-orange-200 gap-1">
                <FileText className="h-3 w-3" /> {paymentLabel(campaignData.paymentType)}
              </Badge>
            ) : (
              <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 gap-1">
                <CreditCard className="h-3 w-3" /> {paymentLabel(campaignData.paymentType)}
              </Badge>
            )}
            {/* {isOffline && (
              <Button size="sm" variant="outline" onClick={() => navigate(`${base_path}/payment-proof/${campaignData.id}`)}>
                <FileText className="h-4 w-4 mr-1" /> View Proof
              </Button>
            )} */}
            <Button
              size="sm"
              onClick={() => generateInvoicePDF(
                campaignData,
                userdata,
                termss,
                buyerEmail,
                buyerPhone,
                invoiceCurrencyCode,
                yourLogo || "",
                termsConfig.showOnDownloadInvoice
              )}
              disabled={isGeneratingPDF}
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
            >
              {isGeneratingPDF ? (
                <>
                  <span className="animate-spin h-3 w-3 border border-white border-t-transparent rounded-full mr-1" />
                  Generating…
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-1" />
                  Download PDF
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Invoice Document */}
        <div className="bg-white shadow-lg rounded-lg overflow-hidden border border-gray-200">

          {/* ── Moving Walls Header ──────────────────────────────────── */}
          <div className="bg-emerald-700 text-white px-8 py-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              {yourLogo ? (
                <img src={yourLogo} alt="Company Logo" className="h-12 mb-2" />
              ) : (
                <div className="text-2xl font-extrabold tracking-tight">MOVING WALLS</div>
              )}
              <div className="mt-2 text-xs text-emerald-100 space-y-0.5">
                <div>{MW.name}</div>
                <div>{MW.uen}</div>
                <div>{MW.addr1}</div>
                <div>{MW.addr2}</div>
                <div>{MW.addr3}</div>
              </div>
            </div>
            <div className="sm:text-right shrink-0">
              <div className="bg-white text-emerald-700 rounded-md px-5 py-3 inline-block text-center min-w-[210px]">
                <div className="text-sm font-bold uppercase tracking-wider">Tax Invoice</div>
                <div className="text-xs text-gray-500 mt-0.5">#{campaignData.invoiceId}</div>
                <div className="text-xs text-gray-400 mt-0.5 break-all">Campaign: {campaignData.code}</div>
                <div className="mt-2 text-xl font-extrabold text-emerald-700">{f(balanceDue)}</div>
                <div className="text-[10px] text-gray-400">Balance Due</div>
              </div>
            </div>
          </div>

          {/* ── Billing Section ──────────────────────────────────────── */}
          <div className="bg-gray-50 px-8 py-5 grid sm:grid-cols-2 gap-6 border-b border-gray-200">
            <div>
              <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Billing To</div>
              <div className="font-bold text-gray-900 text-sm">{buyerName}</div>
              <div className="text-xs text-gray-600 space-y-0.5 mt-1">
                {buyerAddress && <div>{buyerAddress}</div>}
                {buyerLocation && <div>{buyerLocation}</div>}
                <div>{buyerEmail}</div>
              </div>
            </div>
            <div className="sm:border-l sm:border-gray-200 sm:pl-6">
              <div className="grid grid-cols-2 gap-y-1.5 text-xs">
                {[
                  ["Invoice Date", fmtDate(campaignData.startDate)],
                  ["P.O. #", campaignData.name],
                  ["Salesperson", "Marketplace"],
                  ["Payment Type", paymentLabel(campaignData.paymentType)],
                ].map(([label, value]) => (
                  <div key={label} className="contents">
                    <span className="text-gray-500 font-medium">{label}</span>
                    <span className="text-gray-900 font-semibold">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Item Details — grouped by type ──────────────────────── */}
          <div className="px-8 py-5">
            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Item Details</div>

            <div className="space-y-5">
              {Object.entries(grouped).map(([type, items]) => {
                const typeTotal = (items as any[]).reduce((s: number, i: any) => s + i.price, 0);
                return (
                  <div key={type}>
                    {/* Type header */}
                    <div className={`border-l-4 ${typeBorderClass(type)} px-3 py-2 rounded-r-md flex justify-between items-center mb-2`}>
                      <div className="flex items-center gap-2">
                        <Badge className={`text-[10px] px-2 py-0.5 ${typeBadgeClass(type)}`}>{type}</Badge>
                        <span className="text-sm font-semibold text-gray-700">
                          {(items as any[]).length} billboard{(items as any[]).length !== 1 ? "s" : ""}
                        </span>
                      </div>
                      <span className="text-sm font-bold text-emerald-700">{f(typeTotal)}</span>
                    </div>

                    {/* Inventory rows */}
                    <div className="space-y-2 ml-2">
                      {(items as any[]).map((inv: any) => {
                        const moName = getMediaOwner(inv.mediaOwnerName)?.name || inv.mediaOwnerName || "";
                        return (
                          <div key={inv.id} className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition-colors">
                            {/* Thumbnail */}
                            <img src={inv.image} alt={inv.name}
                              className="w-20 h-14 object-cover rounded-md shadow-sm shrink-0" />
                            {/* Info */}
                            <div className="flex-1 min-w-0 space-y-1">
                              <div className="font-bold text-sm text-gray-900">{inv.name}</div>
                              <div className="flex items-center text-xs text-gray-500 gap-1">
                                <MapPin className="h-3 w-3 text-emerald-500 shrink-0" />
                                {inv.inventoryAddress || inv.location}
                              </div>
                              {moName && (
                                <div className="text-xs text-emerald-600 font-medium">by {moName}</div>
                              )}
                              <div className="flex flex-wrap gap-3 text-[11px] text-gray-500 pt-0.5">
                                <span className="flex items-center gap-1">
                                  <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                                  {(inv.impressions || 0).toLocaleString()} impressions
                                </span>
                                <span className="flex items-center gap-1">
                                  <span className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                                  {inv.resolution}
                                </span>
                                <span className="flex items-center gap-1">
                                  <span className="w-1.5 h-1.5 bg-purple-400 rounded-full" />
                                  {spotsLabel(inv)}
                                </span>
                              </div>
                            </div>
                            {/* Price */}
                            <div className="text-right shrink-0">
                              <div className="font-bold text-base text-emerald-700">{f(inv.price)}</div>
                              <div className="text-[11px] text-gray-400 mt-0.5">
                                {fmtDate(campaignData.startDate)} –<br />{fmtDate(campaignData.endDate)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Financial Summary ────────────────────────────────────── */}
          <div className="px-8 pb-6">
            <div className="flex justify-end">
              <div className="w-full max-w-sm border border-gray-200 rounded-md overflow-hidden">
                <table className="w-full text-xs table-fixed">
                  <colgroup>
                    <col className="w-[60%]" />
                    <col className="w-[40%]" />
                  </colgroup>
                  <tbody>
                    <tr className="bg-white">
                      <td className="px-3 py-2 text-gray-600 border-b border-gray-100">Media Cost (Subtotal)</td>
                      <td className="px-3 py-2 text-right text-gray-800 font-medium border-b border-gray-100">{f(mediaCost)}</td>
                    </tr>
                    {hasTax && (
                      <tr className="bg-amber-50">
                        <td className="px-3 py-2 text-amber-700 border-b border-gray-100">{taxName} {Math.round(taxPercent)}% <span className="text-[10px]">({campaignData.country} inventory)</span></td>
                        <td className="px-3 py-2 text-right text-amber-800 font-medium border-b border-gray-100">{f(gst)}</td>
                      </tr>
                    )}
                    <tr className="bg-gray-50">
                      <td className="px-3 py-2 text-gray-800 font-bold border-b border-gray-100">Total Value</td>
                      <td className="px-3 py-2 text-right text-gray-900 font-bold border-b border-gray-100">{f(totalValue)}</td>
                    </tr>
                    {couponDiscount > 0 && (
                      <tr className="bg-green-50">
                        <td className="px-3 py-2 text-green-700 border-b border-gray-100">
                          <span className="flex items-center gap-1">
                            <Tag className="h-3 w-3 shrink-0" /> Coupon
                            <span className="px-1.5 py-0.5 bg-green-100 border border-green-200 rounded text-[10px] font-bold">{campaignData.couponCode}</span>
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right text-green-700 font-bold border-b border-gray-100">-{f(couponDiscount)}</td>
                      </tr>
                    )}
                    <tr className="bg-white">
                      <td className="px-3 py-2 text-gray-600 border-b border-gray-100">Payment Made</td>
                      <td className="px-3 py-2 text-right text-gray-700 border-b border-gray-100">{f(paymentMade)}</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-3 py-2 text-gray-800 font-bold border-b border-gray-100">Grand Total</td>
                      <td className="px-3 py-2 text-right text-gray-900 font-bold border-b border-gray-100">{f(grandTotal)}</td>
                    </tr>
                    <tr className="bg-emerald-700 text-white">
                      <td className="px-3 py-3 font-bold text-sm">Balance Due</td>
                      <td className="px-3 py-3 text-right font-bold text-sm">{f(balanceDue)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Tax note — only shown when tax applies */}
            {hasTax && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800 max-w-xs ml-auto">
                {taxName} ({Math.round(taxPercent)}%) applied on {campaignData.country}-located inventory per local tax regulations.
              </div>
            )}
          </div>

          <Separator />

          {/* ── Footer ─────────────────────────────────────────────────── */}
          <div className="px-8 py-6 bg-gray-50">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <div className="text-sm font-bold text-gray-700 mb-1">Notes</div>
                <div className="text-xs text-gray-600 italic mb-3">Thank you for your business.</div>
                {isFlexiPay ? (
                  <div className="flex items-center gap-2">
                    <Badge className="bg-purple-100 text-purple-800 gap-1"><Zap className="h-3 w-3" />FlexiPay</Badge>
                    {campaignData.bnplStatus && (
                      <Badge className="bg-purple-200 text-purple-900 capitalize">{campaignData.bnplStatus}</Badge>
                    )}
                  </div>
                ) : isOffline ? (
                  <Badge className="bg-orange-100 text-orange-800 gap-1"><FileText className="h-3 w-3" />{paymentLabel(campaignData.paymentType)}</Badge>
                ) : (
                  <Badge className="bg-emerald-100 text-emerald-800 gap-1"><CheckCircle className="h-3 w-3" />{paymentLabel(campaignData.paymentType)} — Payment Completed</Badge>
                )}
              </div>
              <div className="sm:border-l sm:border-gray-200 sm:pl-6">
                <div className="text-sm font-bold text-gray-700 mb-2">Payment Details</div>
                <div className="text-xs text-gray-600 space-y-0.5">
                  <div><span className="font-semibold text-gray-800">Company Name:</span> {OCBC.company}</div>
                  <div><span className="font-semibold text-gray-800">Bank Name:</span> {OCBC.bank}</div>
                  <div><span className="font-semibold text-gray-800">Bank Address:</span> {OCBC.address}</div>
                  <div><span className="font-semibold text-gray-800">Bank A/C No.:</span> {OCBC.sgd} (SGD)</div>
                  <div className="pl-[5.5rem]">{OCBC.usd} (USD)</div>
                  <div><span className="font-semibold text-gray-800">Swift Code:</span> {OCBC.swift}</div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Terms and Conditions Section - Show on page if configured */}
      {termsConfig.showOnPaymentInvoice && termss && termss.trim() && (
        <div className="max-w-5xl mx-auto px-4 py-6">
          <div className="bg-white shadow-lg rounded-lg overflow-hidden border border-gray-200">
            <div className="bg-emerald-700 text-white px-8 py-4">
              <h2 className="text-xl font-bold">Terms & Conditions</h2>
            </div>
            <div className="px-8 py-6 max-h-[600px] overflow-y-auto">
              <div 
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: termss }} 
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}